import React, { useState, useEffect, useRef } from 'react';
import { RecordVideos } from 'components/Video/RecordVideos';
import { TournamentLink } from 'views/Tournament/TournamentLink';
import { GameScoreTabs } from 'views/PvP/game/GameScoreTabs';
import { makeStyles } from '@material-ui/core/styles';
import _ from 'lodash';
import {ConquestGameData, ConquestGameQuery, ConquestGameVars} from '@pvplevelup/core/dist/queries/game/conquest';
import { useQuery } from '@apollo/client';
import { Typography, colors, useMediaQuery } from '@material-ui/core';
import { ZoneInformation, IZone, IMap, ControlPointType, Point } from '@pvplevelup/core/dist/interfaces/maps/IMaps';
import { IPlayerLocation } from '@pvplevelup/core/dist/interfaces/Game/IGame';
import MapRenderer, { MapDataTypes, PointData } from 'components/Map/MapRenderer';
import { MapIcons, MapThumbnailIcons } from 'utilities/map_icons';
import ReactResizeDetector from 'react-resize-detector';
import { GameHeader } from '../PvP/game/GameHeader';
import { WinnerBanner } from '../PvP/game/WinnerBanner';
import { GameProgressBar } from '../PvP/game/GameProgressBar';
import { GameScore, getTeamScores } from '../PvP/game/GameScore';
import { DeathBreakdown } from '../PvP/game/DeathBreakdown';
import { PlayerScore } from '../PvP/game/PvPGame';
import { classImages } from 'utilities/ClassImages';
import { IConquestControlPoint } from '@pvplevelup/core/dist/interfaces/Game/IConquest';
import { RouteComponentProps } from 'react-router-dom';
import { AocClasses } from '@pvplevelup/core/dist/interfaces/ICharacter';
import { TeamColors } from 'utilities/team_colors';
import { breakPoint } from 'components/Character/CharacterStats';
import { blackColor } from 'assets/jss/material-dashboard-react';
import { hhmmss } from '@pvplevelup/core/dist/utility/time';



const useStyles = makeStyles(theme => ({
  root:{
    flexGrow:1
  },
  gameMode: {
    color: 'grey'
  },
  hr: {
    border: '1px solid #424242',
    borderBottom: 0
  },
  mainContent: {
    flex: '1 1 auto',
    overflowY: 'auto'
  },
  footer: {
    height: '40px'
  },
  main: {
    height:'100%'
  },
  noGrow: {
    flex: '0 0 auto'
  },
  background: {
    position: "absolute",
    zIndex: -1,
    height: "100%",
    width: "100%",
    display: "block",
    top: "0",
    left: "0",
    backgroundSize: "cover",
    backgroundPosition: "center center",
    "&:after": {
      position: "absolute",
      zIndex: "3",
      width: "100%",
      height: "100%",
      content: '""',
      display: "block",
      background: blackColor,
      opacity: ".65"
    }
  }
}));

interface Props{
}

interface RouteParams {
  id?: string;
}

const normalise = (value: number, min: number, max: number) => (value - min) * 100 / (max - min);


export const ConquestSummary: React.FC<Props & RouteComponentProps<RouteParams>> = (props) => {
  const classes = useStyles();

  let [currentLocale, setCurrentLocale] = useState<string | undefined>("");
  let [imageWidth, setImageWidth] = useState(5);
  let [containterWidth, setContainerWidth] = useState(0);
  const imageWidthRef = useRef(imageWidth);
  imageWidthRef.current = imageWidth;
  const [imageResizeTimeout, setImageResizeTimeout] = useState<NodeJS.Timeout | undefined>();
  let [isResizing, setIsResizing] = useState(false);

  const [zone, setZone] = useState<IZone | null>(null);
  const [map, setMap] = useState<IMap | null>(null);
  let [ position, setPosition ] = useState<IPlayerLocation | null>(null);
  const positionRef = useRef(position);
  positionRef.current = position;

  let [teamScores, setTeamScores] = useState<Array<PlayerScore[]>>([]);
  let [controlPoints, setControlPoints] = useState<Array<IConquestControlPoint>>([]);
  const controlPointsRef = useRef(controlPoints);
  controlPointsRef.current = controlPoints;

  let [controlling, setControlling] = useState<number[]>([]);
  let [capturing, setCapturing] = useState<number[]>([]);
  let [pointColor, setPointColor] = useState<string>(colors.common.white);

  let [hoveredPoint, setHoveredPoint] = useState<string>("");

  

  const { subscribeToMore, data, error, refetch } = useQuery<ConquestGameData,ConquestGameVars>(
    ConquestGameQuery,
    { variables: {id:props.match.params.id},fetchPolicy:'network-only' }
  );

  useEffect(()=>{
    let z = ZoneInformation[data?.conquest_game?.zone || ""] || null;
    let m = (z) ? z.mapInfo[data?.conquest_game?.map || ""] : null;
    setZone(z);
    setMap(m);
  },[data?.conquest_game?._id])

  const updateScores = () => {
    if(data?.conquest_game){
      let newTeamScores:Array<PlayerScore[]> = getTeamScores(data?.conquest_game?.players || []);
      setTeamScores(newTeamScores);
    }
  }

  useEffect(()=>{
    updateScores();
  },[data?.conquest_game]);

  const matches = useMediaQuery(`(max-width:${breakPoint}px)`);

  let characterClass = data?.conquest_game?.character?.class;

  return (
    <React.Fragment>
      {data?.conquest_game &&
        <section className={"game-container"} style={{
          gridTemplateAreas: matches ?  `
                                          'header header'
                                          'content content'
                                          'sidebar sidebar'
                                        ` 
                                        : `
                                            'header header'
                                            'content sidebar'
                                          `,
        }}>
          <div className="game-information">
            <GameHeader map={map?.name} zone={zone?.name} mode={data.conquest_game?.mode} 
              endCondition={data.conquest_game?.endCondition} time={data.conquest_game?.time}
              scoreToWin={data.conquest_game?.scoreToWin} gameTimer={hhmmss(data.conquest_game?.timeElapsed || 0)} teamMMR={data.conquest_game?.teamMMR} teamCMMR={data.conquest_game?.teamCMMR}
              actions={(data.conquest_game as any)?.tournament ? <TournamentLink tournament={(data.conquest_game as any)?.tournament} /> : undefined}
            />
            {data.conquest_game?.winner != null &&
              <WinnerBanner team={data.conquest_game?.winner} />
            }
          </div>
          <section className="game-content">
            <div className="game-score">
              {(data.conquest_game?.controlPoints?.length || 0) > 0 &&
                <div className="objective-panel" style={{marginBottom: 12}}>
                  <div className="objective-title">Capture Points</div>
                  {data.conquest_game?.controlPoints?.map((p)=>{
                    let value = (p.oneCapturePoints && p.oneCapturePoints > 0) ? p.oneCapturePoints : (p.twoCapturePoints && p.twoCapturePoints > 0) ? p.twoCapturePoints : 0;
                    return <div className="objective-row" key={p.point?.name}>
                      <GameProgressBar label={p.point?.name || ""} color={TeamColors[p.capturing ?? -1]} value={normalise(value,0,(data.conquest_game?.pointsToCapture || 15))} />
                    </div>
                  })}
                </div>
              }
              <GameScoreTabs game={data.conquest_game}><GameScore teamScores={teamScores} game={data.conquest_game} /></GameScoreTabs>
            </div>
            <RecordVideos 
              entries={(data.conquest_game.players || []).map((p: any) => ({user: p.user, name: p.name || p.character?.name, video: p.video}))} />
          </section>
          <div className="game-sidebar">
            <div className="summary-information">
            <DeathBreakdown deaths={data.conquest_game?.deaths} players={data.conquest_game?.players} style={{maxHeight:'75vh'}} />
            </div>
          </div>
          <ReactResizeDetector handleWidth handleHeight targetDomEl={window.document.body} onResize={(width,height) => {
            if(containterWidth != width)
              setIsResizing(true);
            setContainerWidth(width);
          }} />
          <div
            className={classes.background}
            style={{ backgroundImage: "url(" + MapThumbnailIcons[data.conquest_game.zone || ""][data.conquest_game.map || ""] + ")" }}
          />
        </section>
      }
    </React.Fragment>
    
  )
}